import React, { useContext, useEffect, useRef } from 'react';
import {
  Button,
  TextInput,
  InlineLoading,
  NumberInput,
  FormLabel,
  DatePicker,
  DatePickerInput
} from 'carbon-components-react';
import { useFormik } from 'formik';
import { TransactionLimitSchema } from 'utilities/validation/transactionLimit.validation';
import Utils from 'utilities/helper.util';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import { addOrganisationTransactionLimitsStart } from '../../localStore/actions';
import { TransactionLimitTooltip } from '../limitTooltip';

const MAX_LIMIT_STEP = 100;
const MAX_LIMIT_MAX = 500000;
const MAX_LIMIT_MIN = 0;

type transationLimitFormValues = {
  amount?: number;
  endDate: string;
  reference: string;
};

const TransationLimitForm: React.FC<Props> = ({
  onClose,
  onRefreshTransactionLimits,
  organisationId,
  showOverlay
}) => {
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const formSubmited = useRef<boolean>(false);

  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    setFieldTouched,
    isSubmitting,
    handleSubmit
  } = useFormik<transationLimitFormValues>({
    enableReinitialize: true,
    initialValues: {
      reference: '',
      endDate: ''
    },
    validationSchema: TransactionLimitSchema,
    onSubmit: (values: transationLimitFormValues) => {
      const data = {
        reference: values.reference,
        amount: values.amount,
        expired_at: Utils.getNewDate(values.endDate)
          .add(24, 'h')
          .subtract(1, 's')
      };
      localDispatch(
        addOrganisationTransactionLimitsStart({ organisationId, data })
      );
    }
  });

  const { addOrgTransactionLimitsStatus } = localStore;

  const today = new Date();
  const minDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  ).toISOString();

  const handleCancelClick = () => {
    onClose();
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    const newDate = Utils.getNewDate(val[0]).utc().format();
    setField && setFieldValue(key, newDate, true);
    setField && setFieldTouched(key, true);
  };

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  const onNumberInputChange = (
    key: string,
    value: number,
    touched: boolean = false
  ) => {
    setFieldValue(key, Number(value), true);
    if (!touched) {
      setFieldTouched(key, true, false);
    }
  };

  useEffect(() => {
    if (formSubmited.current && addOrgTransactionLimitsStatus === 'initial') {
      onRefreshTransactionLimits();
      onClose();
    }

    if (
      addOrgTransactionLimitsStatus === 'success' ||
      addOrgTransactionLimitsStatus === 'failed'
    ) {
      formSubmited.current = true;
    }
  }, [addOrgTransactionLimitsStatus, onClose, onRefreshTransactionLimits]);

  return (
    <div
      className={`sys-admin-org-new-form-modal${
        !showOverlay ? ' transparent' : ''
      }`}
    >
      <div className="sys-admin-org-new-form-panel">
        <div className="header">Add transaction limit</div>
        <div className="body">
          <div className="limits-input-label">
            <FormLabel>Transactions limits</FormLabel>
            <TransactionLimitTooltip align="center" direction="bottom" />
          </div>
          <div className="new-form-group">
            <div className="limits-input-container">
              <NumberInput
                id="limits-input"
                name="amount"
                label="Number of transactions"
                max={MAX_LIMIT_MAX}
                min={MAX_LIMIT_MIN}
                step={MAX_LIMIT_STEP}
                invalidText={errors.amount}
                invalid={touched.amount && errors.amount !== undefined}
                value={values.amount || 0}
                onChange={(event: any) => {
                  onNumberInputChange(
                    'amount',
                    event.imaginaryTarget.value,
                    touched.amount
                  );
                }}
                onBlur={handleBlur}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="new-form-group">
            <div className="date-picker-container transaction-form">
              <DatePicker
                dateFormat="d/m/Y"
                datePickerType="single"
                className="date-picker"
                minDate={minDate}
                value={
                  values.endDate ? Utils.toLocalDateFormat(values.endDate) : ''
                }
                onChange={(val: any) => {
                  onChangeDate('endDate', val, true);
                }}
                onClose={onCalendarClose}
              >
                <DatePickerInput
                  id="endDate"
                  name="endDate"
                  data-testid="endDate"
                  labelText="End date"
                  placeholder="dd/mm/yyyy"
                  type="text"
                  autoComplete="off"
                  disabled={isSubmitting}
                />
              </DatePicker>
            </div>
          </div>
          <div className="new-form-group">
            <TextInput
              id="limitReferenceCode"
              name="reference"
              labelText="Reference"
              value={values.reference}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
              maxLength={100}
              data-testid="limitReferenceCode"
            />
          </div>
        </div>
        <div className="footer small">
          <Button
            onClick={() => {
              handleSubmit();
            }}
            disabled={
              Utils.isObjectEmpty(touched) ||
              !Utils.isObjectEmpty(errors) ||
              isSubmitting
            }
          >
            {addOrgTransactionLimitsStatus === 'initial' ? (
              'Save changes'
            ) : (
              <InlineLoading
                description={
                  addOrgTransactionLimitsStatus === 'updating'
                    ? 'Saving'
                    : addOrgTransactionLimitsStatus === 'success'
                    ? 'Saved'
                    : 'Failed'
                }
                status={
                  addOrgTransactionLimitsStatus === 'success'
                    ? 'finished'
                    : addOrgTransactionLimitsStatus === 'failed'
                    ? 'error'
                    : 'active'
                }
              />
            )}
          </Button>
          <Button
            kind="secondary"
            onClick={() => handleCancelClick()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

type Props = {
  onClose: () => void;
  onRefreshTransactionLimits: () => void;
  organisationId: number;
  showOverlay: boolean;
};

export default React.memo(TransationLimitForm);
